import { AttendanceEmployee, AvailableVoce } from "@/api/openapi";
import { PspDrawer } from "@/components/pspDrawer/pspDrawer";
import Skeleton from "react-loading-skeleton";
// eslint-disable-next-line max-len
import { useAttendanceDesktopVociDrawer } from "./attendanceDesktopVociDrawer/useAttendanceDesktopVociDrawer";
import PspSelect from "@/components/pspSelect/pspSelect";
import { showTranslated, translate } from "@/config/translator";
import PspTextInput from "@/components/pspTextInput/pspTextInput";
import PspButton from "@/components/pspButton/PspButton";
// eslint-disable-next-line max-len
import { AttendanceVociListElement } from "../../../../components/attendanceVociListElement/attendanceVociListElement";
import ConfirmationDialog from "@/components/confirmationDialog/ConfirmationDialog";

type AttendanceDesktopVociDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  employee: AttendanceEmployee | null;
};

export function AttendanceDesktopVociDrawer({
  isOpen,
  onClose,
  employee,
}: AttendanceDesktopVociDrawerProps) {
  const {
    availableVoci,
    selectedVoceId,
    setSelectedVoceId,
    lang,
    setBaseValue,
    baseValue,
    quantity,
    setQuantity,
    selectedVoce,
    onCloseWrapper,
    onRemoveVoce,
    voci,
    onOpenRemoveVoceDialog,
    onCloseRemoveVoceDialog,
    onAddVoce,
    removeVoceDialogOpen,
    aboutToRemoveVoceIndex,
  } = useAttendanceDesktopVociDrawer({ onClose, employee });

  return (
    <PspDrawer
      isOpen={isOpen}
      onClose={onCloseWrapper}
      title={employee?.name ?? <Skeleton />}
    >
      <div className="flex flex-col gap-8">
        <PspSelect
          label={"voci"}
          value={selectedVoceId}
          onChange={setSelectedVoceId}
          disabled={!availableVoci}
        >
          <>
            <option disabled value={""}>
              {translate("selectVoce", lang)}
            </option>
            {(availableVoci ?? Array.from({ length: 10 })).map(
              (voce: AvailableVoce | undefined, index) => (
                <option key={voce?.id ?? index} value={voce?.id}>
                  {voce ? showTranslated(voce.name, lang) : <Skeleton />}
                </option>
              ),
            )}
          </>
        </PspSelect>

        {selectedVoce && (
          <div className="flex flex-col gap-4">
            <div className="relative">
              <PspTextInput
                type="number"
                hideNumberInputArrows
                value={baseValue}
                onChange={setBaseValue}
                labelString={translate(
                  selectedVoce.voceType === "base_by_quantity"
                    ? "base"
                    : "amount",
                  lang,
                )}
              />
              <div className="absolute bottom-1.5 text-lg font-medium text-font-secondary right-4">
                €
              </div>
            </div>

            {selectedVoce.voceType === "base_by_quantity" && (
              <PspTextInput
                type="number"
                hideNumberInputArrows
                value={quantity}
                onChange={setQuantity}
                labelString={translate("quantity", lang)}
              />
            )}
            <div className="flex flex-row justify-end">
              <PspButton
                appearance="primary"
                onClick={onAddVoce}
                disabled={
                  (selectedVoce.voceType === "base_by_quantity" &&
                    (!baseValue || !quantity)) ||
                  (selectedVoce.voceType === "amount" && !baseValue)
                }
              >
                {translate("add", lang)}
              </PspButton>
            </div>
          </div>
        )}
      </div>

      <div className="h-px bg-neutral my-4 -mx-4" />

      <h2 className="font-semibold text-xl mb-4">
        {translate("enteredVoci", lang)}
      </h2>

      <div className="flex flex-col gap-6">
        {voci?.map((voce, index) => (
          <AttendanceVociListElement
            voce={voce}
            onRemove={() => onOpenRemoveVoceDialog(index)}
          />
        ))}
      </div>
      <ConfirmationDialog
        isOpen={removeVoceDialogOpen}
        onCancel={onCloseRemoveVoceDialog}
        onConfirm={onRemoveVoce}
        messageString={translate("youAreAboutToRemoveVoce", lang, {
          "{voceName}":
            aboutToRemoveVoceIndex && voci
              ? showTranslated(voci[aboutToRemoveVoceIndex].name, lang)
              : "",
          "{employeeName}": employee?.name ?? "",
        })}
        titleString={translate("areYouSure", lang)}
      />
    </PspDrawer>
  );
}
